import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import en from "../i18n/locales/en.json";
import zhHK from "../i18n/locales/zh-HK.json";
import { useLocale } from "../i18n/useLocale";
import LanguageSwitch from "./LanguageSwitch";
import StableLabel from "./StableLabel";
import WordmarkFlip from "./WordmarkFlip";
import { useDemoBooking } from "./DemoBookingDialog";

function both(key) {
  const read = (messages) => key.split(".").reduce((node, part) => node[part], messages);
  return [read(en), read(zhHK)];
}

const LINKS = [
  { to: "/", label: "nav.home" },
  { to: "/about", label: "nav.about" },
  { to: "/solutions", label: "nav.solutions" },
  { to: "/contact", label: "nav.contact" },
];

function normalize(pathname) {
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

const Navbar = () => {
  const { pathname } = useLocation();
  const { path } = useLocale();
  const { t } = useTranslation();
  const { openBooking } = useDemoBooking();
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => {
    if (menuRef.current?.open) menuRef.current.close();
  };

  const openMenu = () => {
    menuRef.current?.showModal();
    setMenuOpen(true);
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 860) closeMenu();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const current = normalize(pathname);
  const items = LINKS.map((item) => {
    const to = path(item.to);
    return (
      <Link
        key={item.to}
        to={to}
        aria-current={current === normalize(to) ? "page" : undefined}
        onClick={closeMenu}
      >
        <StableLabel alternatives={both(item.label)}>{t(item.label)}</StableLabel>
      </Link>
    );
  });

  return (
    <header className="site-header">
      <a className="skip" href="#main">
        {t("nav.skip")}
      </a>
      <div className="wrap">
        <Link className="wordmark" to={path("/")} aria-label="virpluz" onClick={closeMenu}>
          <WordmarkFlip story="full" play="once" />
        </Link>
        <div className="header-actions">
          <nav className="desktop-nav" aria-label={t("nav.primary")}>
            {items}
            <div className="nav-end">
              <button
                type="button"
                className="btn btn-green"
                onClick={() => {
                  closeMenu();
                  openBooking();
                }}
              >
                <StableLabel alternatives={both("nav.bookDemo")}>{t("nav.bookDemo")}</StableLabel>
              </button>
              <LanguageSwitch />
            </div>
          </nav>
          <button
            type="button"
            className="menu-button"
            aria-label={t("nav.openMenu")}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            onClick={openMenu}
          >
            <svg width="22" height="14" viewBox="0 0 22 14" aria-hidden="true">
              <path d="M0 1h22M0 7h22M0 13h22" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
        </div>
      </div>

      <dialog
        id="site-menu"
        className="nav-dialog"
        ref={menuRef}
        onClose={() => setMenuOpen(false)}
      >
        <div className="wrap nav-dialog-bar">
          <Link className="wordmark" to={path("/")} aria-label="virpluz" onClick={closeMenu}>
            <WordmarkFlip story="full" play="when-active" active={menuOpen} />
          </Link>
          <button type="button" className="menu-button" aria-label={t("nav.closeMenu")} onClick={closeMenu}>
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path d="M1 1l16 16M17 1L1 17" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
        </div>
        <div className="wrap">
          <nav aria-label={t("nav.mobile")}>
            {items}
            <div className="nav-end">
              <button
                type="button"
                className="btn btn-green"
                onClick={() => {
                  closeMenu();
                  openBooking();
                }}
              >
                <StableLabel alternatives={both("nav.bookDemo")}>{t("nav.bookDemo")}</StableLabel>
              </button>
              <LanguageSwitch onSwitch={closeMenu} />
            </div>
          </nav>
        </div>
      </dialog>
    </header>
  );
};

export default Navbar;
